import React, { useEffect, useMemo, useState } from 'react'
import { all_routes } from '../../router/all_routes'
import { Link } from 'react-router-dom'
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import { useDispatch, useSelector } from 'react-redux';
import { createUser, getUsers, updateUser, deleteUser } from '../../../core/data/redux/user/userAction';
import { AppDispatch } from '../../../core/data/redux/store';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../core/common/GlobalFunctions/exportTableData';
import { filterData } from '../../../core/common/GlobalFunctions/tableFilters';
import { useAppConfig } from '../../../customhooks/useAppConfig';
import { formatDate } from '../../../core/utils/configFormatters';
import { Button, Drawer, Input, message, Select } from 'antd';
import { fetchFinancialYearsByCompanyId } from '../../../core/data/redux/financialYear/financialYearActions';
import { fetchAllRoles } from '../../../core/data/redux/user/role/roleAction';

type FormState = {
    email: string;
    displayName: string;
    password: string;
    role_id: string;        // <-- string now
    IsActive: number;
    owner_id: string | number;
    fy_id: number;
    mobile: string;
};


const Users = () => {
    const [selectedStatus, setSelectedStatus] = useState('active');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const { user } = useSelector((state: any) => state.auth);
    const { roles } = useSelector((state: any) => state.role);
    const { totalUsers } = useSelector((state: any) => state.user);
    const { activeCompany } = useSelector((state: any) => state.companyDetails);
    const companyId = activeCompany?.company_detail_id
    const dispatch = useDispatch<AppDispatch>();

    // Drawer open state
    const [open, setOpen] = useState(false);

    // Track if editing: null => creating; number => editing userId
    const [editingUserId, setEditingUserId] = useState<number | null>(null);

    // Form state
    const [formData, setFormData] = useState<FormState>({
        email: "",
        displayName: "",
        password: "",
        role_id: "",
        IsActive: 1,
        owner_id: user?.UserId ?? "",
        fy_id: 1,
        mobile: "",
    });

    // Validation errors
    const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            owner_id: user?.UserId ?? "",
        }));
    }, [user]);

    useEffect(() => {
        if (!roles.length) {
            dispatch(fetchAllRoles());
        }
    }, [dispatch, roles.length]);

    // Handle change
    const handleChange = (field: keyof FormState, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // clear field error on change
        setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    useEffect(() => {
        if (companyId) {
            dispatch(fetchFinancialYearsByCompanyId(companyId))
        }
    }, [dispatch, companyId])


    const validate = (isEditMode: boolean) => {
        const newErrors: Partial<Record<keyof FormState, string>> = {};

        // Email required always; but in edit mode it's disabled — still validate presence
        if (!formData.email || !formData.email.trim()) {
            newErrors.email = "Email is required";
        } else {
            // simple email regex
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = "Enter a valid email address";
            }
        }
        if (!formData.displayName || !formData.displayName.trim()) {
            newErrors.displayName = "Display name is required";
        }

        if (!isEditMode) {
            // Password required only on create
            if (!formData.password || !formData.password.trim()) {
                newErrors.password = "Password is required";
            } else if (formData.password.length < 6) {
                newErrors.password = "Password must be at least 6 characters";
            }
        }

        if (!formData.role_id) {
            newErrors.role_id = "Role is required";
        }

        // optional: mobile validation (if provided)
        if (formData.mobile && formData.mobile.trim()) {
            const mobileRegex = /^[0-9+\-()\s]{6,20}$/;
            if (!mobileRegex.test(formData.mobile)) {
                newErrors.mobile = "Enter a valid phone number";
            }
        }
        setErrors(newErrors);
        // return whether valid
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = () => {
        const isEditMode = !!editingUserId;
        if (!validate(isEditMode)) {
            return;
        }
        const payload: any = {
            ...formData,
            company_details_id:companyId,
            role_id: Number(formData.role_id),
            Username: formData.email,
            DisplayName: formData.displayName,
            Email: formData.email,
            mobile: formData.mobile,
        };

        if (isEditMode) {
            delete payload.password;
            // Update
            dispatch(updateUser({ userId: editingUserId as number, userData: payload as any }))
                .unwrap()
                .then(() => {
                    message.success("User updated successfully!");
                    setOpen(false);
                    setEditingUserId(null);
                    setFormData({
                        email: "",
                        displayName: "",
                        password: "",
                        role_id: "",
                        IsActive: 1,
                        owner_id: user?.UserId ?? "",
                        fy_id: 1,
                        mobile: "",
                    });
                    setErrors({});
                    dispatch(getUsers());
                })
                .catch((err: any) => {
                    message.error(err || "Error updating user");
                });
        } else {
            // Create
            dispatch(createUser(payload as any))
                .unwrap()
                .then(() => {
                    message.success("User created successfully!");
                    setOpen(false);
                    setFormData({
                        email: "",
                        displayName: "",
                        password: "",
                        role_id: "",
                        IsActive: 1,
                        owner_id: user?.UserId ?? "",
                        fy_id: 1,
                        mobile: "",
                    });
                    setErrors({});
                    dispatch(getUsers());
                })
                .catch((err: any) => {
                    message.error(err?.error || "Error creating user");
                });
        }
    };

    // Populate form and open drawer for editing
    const handleEdit = (record: any) => {
        setEditingUserId(record?.UserId ?? null);

        setFormData({
            email: record?.Email ?? "",
            displayName: record?.DisplayName ?? "",
            // show masked password in UI but we won't send it
            password: "********",
            // use `role_id` (snake_case) from record and convert to string for Select matching
            role_id: record?.role_id !== undefined && record?.role_id !== null ? String(record.role_id) : "",
            IsActive: record?.IsActive ?? 1,
            owner_id: record?.owner_id ?? user?.UserId ?? "",
            fy_id: record?.fy_id ?? 1,
            mobile: record?.mobile ?? "",
        });

        // clear previous errors
        setErrors({});
        setOpen(true);
    };

    const handleDelete = (userId: number) => {
        const confirmed = window.confirm("Are you sure you want to delete this user?");
        if (!confirmed) return;
        dispatch(deleteUser(userId))
            .unwrap()
            .then(() => {
                message.success("User deleted successfully!");
                dispatch(getUsers());
            })
            .catch((err: any) => {
                console.log({err})
                message.error(err || "Error deleting user");
            });
            
    };

    useEffect(() => {
        dispatch(getUsers());
    }, [dispatch]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const data = totalUsers ? totalUsers : [];

    useEffect(() => {
        // when roles load and we are editing, ensure role_id is set to a matching string
        if (editingUserId && roles?.length) {
            const user = data.find((u: any) => u.UserId === editingUserId);
            if (user) {
                const roleIdStr = user.role_id !== undefined && user.role_id !== null ? String(user.role_id) : "";
                setFormData(prev => ({ ...prev, role_id: roleIdStr }));
            }
        }
    }, [editingUserId, roles, data]);

    const exportUsers = data.map((user: any) => ({
        Name: user.DisplayName || "N/A",
        Email: user.Email || "N/A",
        CreatedAt: user.CreatedAt ? new Date(user.CreatedAt).toISOString().split("T")[0] : "N/A",
        ModifiedAt: user.ModifiedAt ? new Date(user.ModifiedAt).toISOString().split("T")[0] : "N/A",
        IsActive: user.IsActive ? "Yes" : "No",
        mobile_flags: user.mobile_flags || "N/A",
        mobile_code: user.mobile_code || "N/A",
        mobile: user.mobile || "N/A",
        RoleName: user.RoleName || "N/A",
    }));

    const statusFilter = selectedStatus === "active" ? 1 : 0;

    const filteredData = useMemo(() => {
        return filterData(data || [], { IsActive: statusFilter }, "CreatedAt", "CreatedAt", selectedSort);
    }, [data, statusFilter, selectedSort]);

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, data]);

    const config = useAppConfig();
    if (!config) return null;

    const columns = [
        {
            title: "Name",
            dataIndex: "DisplayName",
            render: (text: String, record: any) => (
                <div className="d-flex align-items-center file-name-icon">
                    <Link to="#" className="avatar avatar-md">
                        <img className="img-fluid rounded-circle" alt="img" src={record.EmployeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"} />
                    </Link>

                    <div className="ms-2">
                        <h6 className="fw-medium">
                            <Link to="#">{text}</Link>
                        </h6>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.DisplayName.length - b.DisplayName.length,
        },
        {
            title: "Email",
            dataIndex: "Email",
            sorter: (a: any, b: any) => a.Email.length - b.Email.length,
        },
        {
            title: "Created Date",
            dataIndex: "CreatedAt",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => a.CreatedAt.length - b.CreatedAt.length,
        },
        {
            title: "Role",
            dataIndex: "RoleName",
            render: (text: String, record: any) => (
                <span className={`badge badge-md p-2 fs-10  ${text === 'Employee' ? 'badge-pink-transparent' : 'badge-soft-purple'}`}>{text}</span>
            ),
            sorter: (a: any, b: any) => a.RoleName.length - b.RoleName.length,
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text: string, record: any) => (
                <span className={`badge ${record.IsActive === 0 ? 'badge-danger' : 'badge-success'} d-inline-flex align-items-center badge-xs`}>
                    <i className="ti ti-point-filled me-1" />
                    {record.IsActive === 0 ? 'Inactive' : 'Active'}
                </span>
            ),
        },
        {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: any) => (
                <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-warning" onClick={() => handleEdit(record)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(record.UserId || record.UserId)}>Delete</button>
                </div>
            ),
        }
    ]

    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Users</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Administration</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Users
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="me-2 mb-2">
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        <i className="ti ti-file-export me-1" />
                                        Export
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <button
                                                onClick={() => exportToCSV(exportUsers, 'Users_data')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-pdf me-1" />
                                                Export as CSV
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => exportToExcel(exportUsers, 'Users_data')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as Excel{" "}
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                onClick={() => exportToPDF(exportUsers, 'Users_data')}
                                                className="dropdown-item rounded-1"
                                            >
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as PDF{" "}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="me-2 mb-2">
                                <Button type="primary" onClick={() => {
                                    setEditingUserId(null); setFormData({
                                        email: "",
                                        displayName: "",
                                        password: "",
                                        role_id: "",
                                        IsActive: 1,
                                        owner_id: user?.UserId ?? "",
                                        fy_id: 1,
                                        mobile: "",
                                    }); setErrors({}); setOpen(true);
                                }}>
                                    Add User
                                </Button>
                            </div>

                            <div className="head-icons ms-2">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    {/* /Breadcrumb */}
                    {/* Performance Indicator list */}
                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>User List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown me-3">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedStatus ? selectedStatus.charAt(0).toUpperCase() + selectedStatus.slice(1) : "Select Status"}
                                    </Link>
                                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('active')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Active
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="#"
                                                onClick={() => setSelectedStatus('inactive')}
                                                className="dropdown-item rounded-1"
                                            >
                                                Inactive
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <div className="dropdown">
                                    <Link
                                        to="#"
                                        className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                                        data-bs-toggle="dropdown"
                                    >
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                                Recently Added
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                                Ascending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                                Descending
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">
                                                Last Month
                                            </Link>
                                        </li>
                                        <li>
                                            <Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">
                                                Last 7 Days
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={true} />
                        </div>
                    </div>
                    {/* /Performance Indicator list */}
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* /Page Wrapper */}

            <Drawer
                title={editingUserId ? "Edit User" : "Add User"}
                placement="right"
                width={400}
                onClose={() => { setOpen(false); setEditingUserId(null); setErrors({}); }}
                open={open}
                style={{ marginTop: "50px", paddingBottom: "20px" }}
            >
                <div className="mb-3">
                    <label>Email</label>
                    <Input
                        value={formData.email}
                        onChange={e => handleChange("email", e.target.value)}
                        disabled={!!editingUserId} // do not allow change in edit mode
                    />
                    {errors.email && <div className="text-danger small mt-1">{errors.email}</div>}
                </div>



                {/* Username input removed */}
                <div className="mb-3">
                    <label>Password</label>
                    {/* In edit mode we show masked value and disable editing */}
                    <Input.Password
                        value={formData.password}
                        onChange={e => handleChange("password", e.target.value)}
                        disabled={!!editingUserId}
                        placeholder={editingUserId ? "********" : "Enter password"}
                    />
                    {errors.password && <div className="text-danger small mt-1">{errors.password}</div>}
                    {editingUserId && <div className="small text-muted mt-1">Password cannot be changed here.</div>}
                </div>

                <div className="mb-3">
                    <label>Display Name</label>
                    <Input
                        value={formData.displayName}
                        onChange={e => handleChange("displayName", e.target.value)}
                    />
                    {errors.displayName && <div className="text-danger small mt-1">{errors.displayName}</div>}
                </div>

                <div className="mb-3">
                    <label>Role</label>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select Role"
                        value={formData.role_id}
                        onChange={(value) => handleChange("role_id", value)}
                        options={roles?.map((role: any) => ({
                            value: String(role.RoleId),   // string values
                            label: role.RoleName
                        })) || []}
                    />


                    {errors.role_id && <div className="text-danger small mt-1">{errors.role_id}</div>}
                </div>

                <div className="mb-3">
                    <label>Phone</label>
                    <Input
                        value={formData.mobile}
                        onChange={e => handleChange("mobile", e.target.value)}
                    />
                    {errors.mobile && <div className="text-danger small mt-1">{errors.mobile}</div>}
                </div>
                <div className="mb-3">
                    <label>Status</label>
                    <Select
                        style={{ width: "100%" }}
                        value={formData.IsActive}
                        onChange={(val) => handleChange("IsActive", val)}
                        options={[
                            { value: 1, label: "Active" },
                            { value: 0, label: "Inactive" }
                        ]}
                    />
                </div>

                <Button type="primary" block onClick={handleSubmit}>
                    {editingUserId ? "Update User" : "Create User"}
                </Button>
            </Drawer>
        </>

    )
}

export default Users
